<template>
  <v-form ref="form">
    <v-row class="pa-0 ma-0">
      <v-col
        v-if="showHeader"
        cols="12"
        class="d-flex align-center justify-space-between"
      >
        <PageTitle v-if="!hideTitle" weight="regular" titleSize="h2">
          {{ title }}
        </PageTitle>
        <v-btn
          v-if="showClose"
          icon
          aria-label="Fermer"
          @click="$emit('cancel')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-col>

      <v-col cols="12" :md="compact ? 12 : 2" :sm="compact ? 12 : 4">
        <v-select
          v-model="taskEntry.status"
          :items="$listUtils.elementStatus"
          label="Statut"
          :rules="$fieldsRules.required('statut')"
          dense
        />
      </v-col>
      <v-col cols="12" :md="compact ? 12 : 8">
        <v-text-field
          v-model="taskEntry.task"
          label="Tâche"
          hint="Nom de la tâche • ex: Appeler M. Dupont"
          :rules="$fieldsRules.required('tâche', true)"
          dense
        />
      </v-col>
      <v-col cols="12" :md="compact ? 12 : 2">
        <v-select
          v-model="taskEntry.category"
          :items="categoryItems"
          item-text="text"
          item-value="value"
          label="Catégorie"
          :rules="categoryRules"
          dense
        />
      </v-col>

      <v-col cols="12" :sm="compact ? 12 : 5">
        <v-autocomplete
          v-model="taskEntry.participants"
          clearable
          multiple
          :items="people"
          chips
          deletable-chips
          color="blue-grey lighten-2"
          label="Participants"
          item-text="name"
          item-value="id"
          dense
        >
          <template v-slot:selection="data">
            <v-chip
              v-bind="data.attrs"
              :input-value="data.selected"
              @click="data.select"
            >
              <v-avatar left>
                <span
                  v-if="!data.item.avatar && data.item.firstName"
                  class="font-weight-regular"
                >{{ $functions.getInitials(data.item.firstName + ' ' + (data.item.lastName || '')) }}</span>
                <span
                  v-else-if="!data.item.avatar && data.item.name"
                  class="font-weight-regular"
                >{{ $functions.getInitials(data.item.name) }}</span>
                <v-img
                  v-else
                  :src="$functions.getImgLink(data.item.avatar, 50)"
                />
              </v-avatar>
              {{ data.item.name }}
            </v-chip>
          </template>
          <template v-slot:item="data">
            <template v-if="data.header">
              <v-list-item-content>{{ data.item }}</v-list-item-content>
            </template>
            <template v-else>
              <v-list-item-avatar>
                <span
                  v-if="!data.item.avatar && data.item.firstName"
                  class="font-weight-regular"
                >{{ $functions.getInitials(data.item.firstName + ' ' + (data.item.lastName || '')) }}</span>
                <span
                  v-else-if="!data.item.avatar && data.item.name"
                  class="font-weight-regular"
                >{{ $functions.getInitials(data.item.name) }}</span>
                <v-img
                  v-else
                  contain
                  :src="$functions.getImgLink(data.item.avatar, 50)"
                />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                <v-list-item-subtitle
                  v-if="data.item.denom != 'utilisateur'"
                  v-html="'Numéro ' + data.item.denom + ' : ' + data.item.content"
                />
              </v-list-item-content>
            </template>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" :sm="compact ? 12 : 4">
        <v-autocomplete
          v-model="taskEntry.project"
          clearable
          :items="projects"
          chips
          color="blue-grey lighten-2"
          label="Projet"
          item-text="name"
          item-value="id"
          dense
        >
          <template v-slot:selection="data">
            <v-chip
              v-bind="data.attrs"
              :input-value="data.selected"
              @click="data.select"
            >
              <v-avatar left>
                <span
                  v-if="!data.item.avatar && data.item.name"
                  class="font-weight-regular"
                >{{ $functions.getInitials(data.item.name) }}</span>
                <v-img
                  v-else
                  :src="$functions.getImgLink(data.item.avatar, 50)"
                />
              </v-avatar>
              {{ data.item.name }}
            </v-chip>
          </template>
          <template v-slot:item="data">
            <template v-if="data.header">
              <v-list-item-content>{{ data.item }}</v-list-item-content>
            </template>
            <template v-else>
              <v-list-item-avatar>
                <span
                  v-if="!data.item.avatar && data.item.name"
                  class="font-weight-regular"
                >{{ $functions.getInitials(data.item.name) }}</span>
                <v-img
                  v-else
                  contain
                  :src="$functions.getImgLink(data.item.avatar, 50)"
                />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ data.item.name }}</v-list-item-title>
              </v-list-item-content>
            </template>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12" :sm="compact ? 12 : 3">
        <v-autocomplete
          v-model="company"
          :items="companies"
          chips
          color="blue-grey lighten-2"
          label="Destination de la tâche"
          hint="Entreprise proriétaire de la tâche"
          item-text="name"
          item-value="id"
          dense
        >
          <template v-slot:selection="data">
            <v-chip
              v-bind="data.attrs"
              :input-value="data.selected"
              @click="data.select"
            >
              <v-avatar left>
                <span
                  v-if="!data.item.avatar && data.item.name"
                  class="font-weight-regular"
                >{{ $functions.getInitials(data.item.name) }}</span>
                <v-img
                  v-else
                  :src="$functions.getImgLink(data.item.avatar, 50)"
                />
              </v-avatar>
              {{ data.item.name }}
            </v-chip>
          </template>
          <template v-slot:item="data">
            <v-list-item-avatar>
              <span
                v-if="!data.item.avatar && data.item.name"
                class="font-weight-regular"
              >{{ $functions.getInitials(data.item.name) }}</span>
              <v-img
                v-else
                contain
                :src="$functions.getImgLink(data.item.avatar, 50)"
              />
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ data.item.name }}</v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="12">
        <PageTitle
          v-if="!compact"
          weight="regular"
          titleSize="h2"
        >
          Details
        </PageTitle>
        <v-card outlined>
          <vue-editor id="vueEditor" v-model="taskEntry.content" />
        </v-card>
      </v-col>

      <v-col cols="12" :md="compact ? 12 : 5">
        <v-select
          v-model="taskEntry.priority"
          :items="$listUtils.priority"
          label="Priorité"
          :rules="$fieldsRules.required('priorité', true)"
          dense
        />
      </v-col>

      <v-col cols="12" :md="compact ? 12 : 5">
        <v-row>
          <v-col cols="12" :sm="compact ? 12 : 6">
            <v-menu
              ref="menu"
              v-model="menu"
              :close-on-content-click="false"
              :return-value.sync="taskEntry.dueDate"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formatDate"
                  label="Date limite"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  dense
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="taskEntry.dueDate"
                first-day-of-week="1"
                scrollable
                @change="$refs.menu.save(taskEntry.dueDate)"
              />
            </v-menu>
          </v-col>
          <v-col v-if="taskEntry.dueDate" cols="12" :sm="compact ? 12 : 6">
            <v-menu
              ref="menu_1"
              v-model="datePickerModal"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="time"
              transition="scale-transition"
              max-width="290"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="time"
                  label="Heure limite"
                  prepend-inner-icon="mdi-clock-time-four-outline"
                  readonly
                  dense
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-time-picker
                v-if="datePickerModal"
                v-model="time"
                format="24hr"
                @click:minute="$refs.menu_1.save(time)"
              />
            </v-menu>
          </v-col>
        </v-row>
      </v-col>

      <v-col
        cols="12"
        class="d-flex flex-column flex-sm-row justify-end"
      >
        <v-btn
          v-if="showCancel"
          class="mb-2 mb-sm-0 mr-sm-2"
          outlined
          @click="$emit('cancel')"
        >
          Annuler
        </v-btn>
        <v-btn
          class="neon"
          outlined
          color="primary"
          :block="compact"
          @click="submitTask()"
        >
          Ajouter
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { setSnackbar } from "@/stores/ui";
import { VueEditor } from "vue2-editor";
import PageTitle from "@/components/graphics/PageTitle";
import { capture } from "@/posthog";
import { mapStores } from "pinia";
import { useTasksStore } from "@/stores/tasks";

export default {
  name: "ComponentTaskForm",
  props: {
    userInfo: {
      type: Object,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showClose: {
      type: Boolean,
      default: false,
    },
    showCancel: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: "Ajouter une Tâche",
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    PageTitle,
    VueEditor,
  },
  data() {
    return {
      menu: false,
      time: "23:59",
      datePickerModal: false,
      taskEntry: this.blankTask(),
      people: [],
      users: [],
      projects: [],
      company: "",
      companies: [],
      clientPeople: [],
      companyPeople: [],
    };
  },
  computed: {
    ...mapStores(useTasksStore),
    categoryItems() {
      return this.$listUtils.itemCategory.map((cat, value) => ({
        text: cat.label,
        value,
      }));
    },
    categoryRules() {
      return [
        (v) =>
          (v !== null && v !== undefined && v !== "") ||
          "Une catégorie est requise.",
      ];
    },
    formatDate() {
      return this.taskEntry.dueDate
        ? this.$moment(this.taskEntry.dueDate).format("DD/MM/YYYY")
        : "";
    },
    dateTime() {
      return new Date(`${this.taskEntry.dueDate} ${this.time}`);
    },
  },
  methods: {
    blankTask() {
      return {
        ...this.$models.task,
        priority: this.$listUtils.priority[2],
        status: this.$listUtils.elementStatus[1],
      };
    },
    resetForm() {
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.taskEntry = this.blankTask();
      this.time = "23:59";
    },
    submitTask() {
      if (!this.$refs.form.validate()) return;

      const task = {
        ...this.taskEntry,
        status: this.$listUtils.elementStatus.indexOf(this.taskEntry.status),
        category: Number(this.taskEntry.category),
        priority: this.$listUtils.priority.indexOf(this.taskEntry.priority),
        creationDate: new Date(),
        lastUpdate: new Date(),
        author: this.userInfo.id,
      };

      if (!task.participants || task.participants.length === 0)
        delete task.participants;
      if (!task.project) delete task.project;
      if (this.taskEntry.dueDate) task.dueDate = new Date(this.dateTime);

      this.$api
        .apiUser({
          method: "post",
          url: `/companies/${this.company}/tasks`,
          data: task,
        })
        .then(() => {
          setSnackbar({
            text: `La tâche ${task.task} a été ajouté !`,
          });

          /* eslint-disable camelcase */
          capture("task_created", {
            category: task.category,
            priority: task.priority,
            status: task.status,
            has_project: !!task.project,
            has_due_date: !!task.dueDate,
            participant_count: task.participants?.length || 0,
          });
          /* eslint-enable camelcase */

          this.resetForm();
          this.$emit("created");
        });
    },
    init() {
      if (!this.company) return;

      this.people = [];
      this.users = [];
      this.projects = [];

      this.$api
        .apiUser({
          method: "get",
          url: `/companies/${this.company}/members`,
        })
        .then((res) => {
          this.users = [
            { header: "Utilisateurs" },
            ...(res.data.documents || []).map((member) => ({
              name: `${(member.user?.lastName || "").toUpperCase()} ${member.user?.firstName || ""}`.trim(),
              id: member.id,
              avatar: member.user?.avatar,
              denom: "utilisateur",
            })),
          ];
          this.composePeople();
        })
        .catch(() => {
          setSnackbar({
            text: "Impossible de charger les participants",
            color: "error",
          });
          this.composePeople();
        });

      if (this.unsubCustomers) this.unsubCustomers();
      this.unsubCustomers = this.$live.watch(
        this.company,
        "customers",
        (res) => {
          this.clientPeople = res.docs.map((document) => ({
            ...document.data(),
            name:
              (document.data().lastName || "").toUpperCase() +
              " " +
              (document.data().firstName || ""),
            content: document.data().nClient,
            id: document.id,
            avatar: document.data().avatar,
            denom: "client",
          }));
          this.composePeople();
        },
      );

      if (this.unsubCompanies) this.unsubCompanies();
      this.unsubCompanies = this.$live.watch(
        this.company,
        "companies",
        (res) => {
          this.companyPeople = res.docs.map((document) => ({
            ...document.data(),
            name: document.data().name,
            content: document.data().nCompany,
            id: document.id,
            avatar: document.data().avatar,
            denom: "entreprise",
          }));
          this.composePeople();
        },
      );

      if (this.unsubProjects) this.unsubProjects();
      this.unsubProjects = this.$live.watch(this.company, "projects", (res) => {
        this.projects = res.docs.map((document) => ({
          name: document.data().title,
          id: document.id,
          avatar: document.data().avatar,
        }));
      });
    },
    composePeople() {
      this.people = [
        ...(this.users || []),
        { header: "Clients" },
        ...(this.clientPeople || []),
        { header: "Entreprises" },
        ...(this.companyPeople || []),
      ];
      this.tasksStore.seedPeople(this.people);
    },
    loadCompanies() {
      this.companies = [];
      this.company = this.userInfo.company;
      this.$api
        .apiUser({
          method: "get",
          url: `/users/${this.userInfo.id}/companies`,
        })
        .then((res) => {
          this.companies = (res.data.documents || [])
            .slice()
            .sort((a, b) =>
              a.name.toUpperCase().localeCompare(b.name.toUpperCase()),
            );
        });
    },
  },
  watch: {
    userInfo: {
      deep: true,
      immediate: true,
      handler(info) {
        if (!info?.company) return;
        this.company = info.company;
        this.loadCompanies();
        this.init();
      },
    },
    company(val, oldVal) {
      if (val && val !== oldVal) this.init();
    },
    users() {
      this.composePeople();
    },
  },
  beforeUnmount() {
    if (this.unsubCustomers) this.unsubCustomers();
    if (this.unsubCompanies) this.unsubCompanies();
    if (this.unsubProjects) this.unsubProjects();
  },
};
</script>

<style scoped>
::v-deep .ql-toolbar {
  flex-wrap: wrap;
  white-space: normal;
}

::v-deep .ql-toolbar.ql-snow,
::v-deep .ql-container.ql-snow {
  max-width: 100%;
}
</style>
